// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit, Manufacturer } from '@evolvemep/api-ts';
import { TextInput, getDefaultZIndex } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';

type Props = {
  manufacturerName: string;
  opened: boolean;
  onClose: () => void;
  onCreate: (manufacturer: Manufacturer) => void;
};

const NewManufacturerForm = ({ onCreate, ...initialValues }: StrongOmit<Props, 'opened'>) => {
  const { addManufacturer } = useManufacturers();
  const form = useForm<{
    manufacturerName: string;
  }>({
    initialValues,
    initialDirty: {
      manufacturerName: true,
    },
    validate: { manufacturerName: (n) => !n },
  });

  const onSubmit = async ({ manufacturerName }: typeof form.values) => {
    await addManufacturer(manufacturerName).then(onCreate);
  };

  useFormModal(form, onSubmit);

  return (
    <TextInput
      label="Manufacturer Name"
      autoFocus
      data-autofocus
      required
      maxLength={64}
      {...form.getInputProps('manufacturerName')}
    />
  );
};

export const NewManufacturerModal = ({ opened, ...props }: Props) => {
  // This modal is typically used inside a Popover,
  // so we need to have a z-index higher than the Popover
  const popoverZIndex = getDefaultZIndex('popover');
  return (
    <FormModal
      title="New manufacturer"
      opened={opened}
      onClose={props.onClose}
      zIndex={popoverZIndex + 2}
      overlayProps={{ zIndex: popoverZIndex + 1 }}
      type="submit"
      confirmationText="Create"
      size="sm"
    >
      <NewManufacturerForm {...props} />
    </FormModal>
  );
};
